import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Heart, Factory, ShoppingBag, Landmark, Cpu, Zap, Truck, ArrowUpRight, Sparkles } from 'lucide-react';
import api, { resolveMedia } from '../lib/api';

export const INDUSTRIES = [
  { name: 'Financial Services', icon: Building2 },
  { name: 'Healthcare', icon: Heart },
  { name: 'Manufacturing', icon: Factory },
  { name: 'Retail & E-commerce', icon: ShoppingBag },
  { name: 'Government & Public Sector', icon: Landmark },
  { name: 'Technology & SaaS', icon: Cpu },
  { name: 'Energy & Utilities', icon: Zap },
  { name: 'Logistics', icon: Truck }
];

const iconFor = (industry) => {
  const match = INDUSTRIES.find((i) => i.name === industry);
  return match ? match.icon : Sparkles;
};

const CaseStudies = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    let alive = true;
    api.get('/case-studies').then((r) => {
      if (alive) setItems(r.data || []);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const industriesWithCount = useMemo(() => {
    const counts = {};
    items.forEach((i) => { counts[i.industry] = (counts[i.industry] || 0) + 1; });
    return counts;
  }, [items]);

  const filtered = filter === 'All' ? items : items.filter((i) => i.industry === filter);

  return (
    <section id="case-studies" className="relative py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="section-label mb-6">Case studies</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight max-w-3xl">
            Real outcomes across <span className="gradient-text">every industry.</span>
          </h2>
          <p className="text-slate-400 max-w-md text-sm leading-relaxed">
            Filter by your industry to see how teams like yours modernized their cloud, cut costs,
            and shipped AI without the noise.
          </p>
        </div>

        {/* Industry filter chips */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="case-study-filters">
          <button
            onClick={() => setFilter('All')}
            data-testid="filter-all"
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${
              filter === 'All'
                ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                : 'bg-white/5 text-slate-300 border-white/10 hover:border-amber-500/30'
            }`}
          >
            All {items.length ? `(${items.length})` : ''}
          </button>
          {INDUSTRIES.map(({ name, icon: Icon }) => {
            const count = industriesWithCount[name] || 0;
            const active = filter === name;
            return (
              <button
                key={name}
                onClick={() => setFilter(name)}
                data-testid={`filter-${name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border transition-colors ${
                  active
                    ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                    : 'bg-white/5 text-slate-300 border-white/10 hover:border-amber-500/30'
                }`}
              >
                <Icon size={14} />
                {name}
                {count > 0 && <span className="text-[10px] text-slate-500 ml-0.5">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-slate-500 py-16 text-center text-sm">Loading case studies…</div>
        ) : filtered.length === 0 ? (
          <div className="card-dark rounded-3xl p-12 text-center">
            <div className="h-14 w-14 rounded-full bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-amber-300" size={22} />
            </div>
            <p className="text-white text-base font-semibold">More stories coming soon.</p>
            <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
              We&apos;re publishing new case studies every month. Talk to us to hear the ones we can&apos;t print yet.
            </p>
            <a href="#contact" className="inline-flex items-center gap-1.5 text-amber-300 text-sm mt-5 hover:text-amber-200">
              Get in touch <ArrowUpRight size={14} />
            </a>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => {
              const Icon = iconFor(c.industry);
              return (
                <Link
                  key={c.id}
                  to={`/case-studies/${c.slug || c.id}`}
                  data-testid={`case-study-${c.id}`}
                  className="card-dark rounded-3xl overflow-hidden hover:border-amber-500/30 transition-colors group flex flex-col"
                >
                  <div className="relative aspect-[16/9] bg-gradient-to-br from-amber-500/10 to-transparent border-b border-white/5 overflow-hidden">
                    {c.thumbnail ? (
                      <img
                        src={resolveMedia(c.thumbnail)}
                        alt={c.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="text-amber-300/40" size={52} strokeWidth={1.3} />
                      </div>
                    )}
                    {c.featured && (
                      <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500/25 text-amber-100 border border-amber-500/40 backdrop-blur-sm">
                        Featured
                      </span>
                    )}
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-black/50 text-slate-200 border border-white/10 backdrop-blur-sm">
                      <Icon size={11} /> {c.industry}
                    </span>
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-white text-lg font-display font-semibold leading-snug mb-2 group-hover:text-amber-200 transition-colors">{c.title}</h3>
                    {c.client && (
                      <div className="text-slate-500 text-xs mb-3">Client: {c.client}</div>
                    )}
                    {c.outcome && (
                      <p className="text-slate-400 text-sm leading-relaxed line-clamp-3 mb-4">{c.outcome}</p>
                    )}
                    {Array.isArray(c.metrics) && c.metrics.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {c.metrics.slice(0, 3).map((m) => (
                          <span key={m} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/25">
                            {m}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between">
                      <div className="flex flex-wrap gap-1.5">
                        {(c.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">
                            {t}
                          </span>
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-amber-300 text-sm shrink-0 group-hover:translate-x-0.5 transition-transform">
                        Read <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudies;
